import React, { useState, useEffect } from 'react';
import {
	Box,
	CircularProgress,
	Link,
} from '@mui/material';
import Text from '../text';
import ApiService from 'utils/services/ApiService';
import LabRequestForm from './LabRequestForm';
import PrimaryButton from '../button/PrimaryButton';
import PackingSlip from './PackingSlip';
import LabRequestDetails from './LabRequestDetails';
import { addEventLog } from 'utils/functions/eventLogFunctions';

interface LabRequestProps {
	isChampionX?: boolean;
}

export interface CocSample {
	//Industrial
    inventoryNumber: number;
    name?: string;
    type?: string;
    amount?: string;
    units?: string;
    preserved?: boolean;
    siteId?: string;
    locationId?: string;
    siteName?: string;
    locationName?: string;
    newSite?: boolean;
    newLocation?: boolean;
    //Food lab
    clientSampleId?: string;
    shelfLife?: string;
    recStorageConditions?: string;
    customAnalysis?: string;
    //Shared
    collectionDate: any;
    cocAnalysisVOs: CocAnalysisVO[];
	inventoryId?: string;
	comments?: string;
}

export interface CocAnalysisVO {
	name: string,
	variant: string,
	id: number
}

export interface CocForm {
	labId: number | null,
	submissionDate: any,
	contactName: string,
	company: string,
	email: string,
	purchaseOrderNumber: string,
	quoteNumber: string,
	costCenterNumber?: string,
	rushOrder: string,
	specialRequests: string,
	unpreservedInstructions: string,
	cocId: string,
	collaboratorEmails: string[],
	cocSamples: CocSample[],
	cocAnalysisVOs: CocAnalysisVO[],
	sameAnalysesForAll: boolean,
	addressInfo: {
		streetAddress1: string,
		streetAddress2: string,
		city: string,
		officePhone: string,
		countryName: string,
		postalCode: string,
		provinceName: string,
		id: number
	}
}

export interface CocSite {
	id: number;
	name: string;
	sampleLocationAllVOs: {
		id: number;
		name: string;
	}[];
}
const FOOD_LAB_ENABLED = false;
const LabRequests: React.FC<LabRequestProps> = ({
	isChampionX = false,
}) => {
	const [loading, setLoading] = useState(false);
	const [requestForms, setRequestForms] = useState<CocForm[]>([]);
	const [latestForm, setLatestForm] = useState<CocForm>({
		labId: 1,
		submissionDate: "",
		contactName: "",
		company: "",
		email: "",
		purchaseOrderNumber: "",
		quoteNumber: "",
		costCenterNumber: "",
		rushOrder: "",
		specialRequests: "",
		unpreservedInstructions: "",
		cocId: "",
		collaboratorEmails: ([]),
		cocSamples: ([]),
		cocAnalysisVOs: ([]),
		sameAnalysesForAll: false,
		addressInfo: {
			streetAddress1: "",
			streetAddress2: "",
			city: "",
			officePhone: "",
			countryName: "",
			postalCode: "",
			provinceName: "",
			id: 0
		}
	});
	const [isFoodLab, setIsFoodLab] = useState(false);
	const [cocSites, setCocSites] = useState<CocSite[]>([]);
	const [isLabRequestOpen, setIsLabRequestOpen] = useState(false);
	const [isPackingSlipOpen, setIsPackingSlipOpen] = useState(false);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const [cocId, setCocId] = useState("");
	const [submissionDate, setSubmissionDate] = useState("");
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [cocForm, setCocForm] = useState<CocForm>({
		labId: 1,
		submissionDate: "",
		contactName: "",
		company: "",
		email: "",
		purchaseOrderNumber: "",
		quoteNumber: "",
		rushOrder: "",
		specialRequests: "",
		unpreservedInstructions: "",
		cocId: "",
		collaboratorEmails: ([]),
		cocSamples: ([]),
		cocAnalysisVOs: ([]),
		sameAnalysesForAll: false,
		addressInfo: {
			streetAddress1: "",
			streetAddress2: "",
			city: "",
			officePhone: "",
			countryName: "",
			postalCode: "",
			provinceName: "",
			id: 0
		}
	});

	let firstLoad = true;
	useEffect(() => {
		if (firstLoad) {
			loadForms();
			loadCocSites();
			firstLoad = false;
		}

	}, []);

	async function loadForms() {
		try {
			setLoading(true);
			const response = await ApiService.getCocForms();
			let forms: CocForm[] = response.data;
			forms.sort((a, b) => b.submissionDate - a.submissionDate);
			setRequestForms(forms);
			const prefill = FOOD_LAB_ENABLED ? forms[0] : forms.find(f => f.labId !== 2);
			if (prefill) {
				setLatestForm(prefill);
			}
		} catch (error) {
			console.error('Error loading lab request forms:', error);
		} finally {
			setLoading(false);
		}
	}

	async function loadCocSites() {
		try {
			const response = await ApiService.getCocSites();
			setCocSites(response.data);
		} catch (error) {
			console.error('Error loading CoC sites from DB', error);
		}
	}

	function packingSlipClick (form: CocForm) {
		setCocId(form.cocId);
		setSubmissionDate(form.submissionDate);
		setContactName(form.contactName);
		setContactEmail(form.email);
		setIsPackingSlipOpen(true);
	}

	function detailsClick (form: CocForm) {
		setCocForm(form);
		setIsFoodLab(form.labId === 2);
		setIsDetailsOpen(true);
	}

	let dateFormatOptions = new Intl.DateTimeFormat("en-US", {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: true
	});

	return (
		<div id="lab-requests">

			<Box sx={{float: "right"}}>
				<PrimaryButton
					onClick={() => {
						addEventLog(`New CoC Started`, "Lab Request", undefined, undefined);
						setIsLabRequestOpen(true);
					}}
					addStyle="!h-[40px] !w-[180px] !text-white !ml-4 !top-[-24px] !right-[24px]"
					disabled={!navigator.onLine}
				>
					NEW LAB REQUEST
				</PrimaryButton>
			</Box>
			<LabRequestForm
				isOpen={isLabRequestOpen}
				onClose={() => {
					setIsLabRequestOpen(false);
					loadForms();
					loadCocSites();
				}}
				onSubmitSuccess={(submittedCocForm, isFoodLab) => {
					setIsFoodLab(FOOD_LAB_ENABLED && isFoodLab);
				}}
				title="Testing Request / Chain of Custody"
				isChampionX={isChampionX}
				foodLabEnabled={FOOD_LAB_ENABLED}
				cocSites={cocSites}
				latestForm={latestForm}
				maxWidthOverride={1300}
			/>

			<PackingSlip
				cocId = {cocId}
				submissionDate = {submissionDate}
				contactName = {contactName}
				contactEmail = {contactEmail}
				isOpen = {isPackingSlipOpen}
				onClose = {() => setIsPackingSlipOpen(false)}
				maxWidthOverride = {620}
			/>

			<LabRequestDetails
				isFoodLab = {isFoodLab}
				cocForm = {cocForm}
				isChampionX = {isChampionX}
				isOpen = {isDetailsOpen}
				onClose = {() => setIsDetailsOpen(false)}
				maxWidthOverride = {800}
			/>

			<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
				View Requests
			</Text>

			<Box mb={3} className="no-print">
				<Box sx={{ overflowX: 'auto', mb: 1 }}>

					<table style={{ width: '100%', borderCollapse: 'collapse' }}>
						<thead>
							<tr style={{ background: '#f5f5f5' }}>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Date</th>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>CoC #</th>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Lab</th>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Number of Samples</th>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}></th>
								<th style={{ fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}></th>
							</tr>
						</thead>

						<tbody>

							{requestForms.map((form) => (
								<tr key={form.cocId} style={{ borderBottom: '1px solid #e0e0e0' }}>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center' }}>
										{dateFormatOptions.format(new Date(form.submissionDate)).replace(', ', ' - ')}
									</td>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center' }}>
										{form.cocId}
									</td>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center' }}>
										{form.labId === 2 ? 'Food Lab' : 'Industrial Lab'}
									</td>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center' }}>
										{form.cocSamples.length}
									</td>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center'}}>
										<Link 
											style={{cursor:'pointer'}} 
											onClick={() => {
												detailsClick(form);
												addEventLog(`CoC Details Opened`, "Lab Request", undefined, undefined, `CoC #: ${form.cocId}`);
											}}
										>
											Details
										</Link>
									</td>
									<td style={{ margin: 'auto', padding: '8px', textAlign: 'center'}}>
										<Link 
											style={{cursor:'pointer'}} 
											onClick={() => {
												packingSlipClick(form);
												addEventLog(`CoC Packing Slip Opened`, "Lab Request", undefined, undefined, `CoC #: ${form.cocId}`);
											}}
										>
											Packing Slip
										</Link>
									</td>
								</tr>
							))}
							
						</tbody>

					</table>

				</Box>

				{loading && <Box sx={{ textAlign: 'center', padding: '8px' }}>
					<CircularProgress
						size={60}
						color="primary"
						className="!ml-2"
					/>
				</Box>}

			</Box>

		</div>
	);
};

export default LabRequests;
